/**
 * DataTable — shared table for a list of records.
 *
 * Columns are data, not markup: pass an array and the table renders it. That is what lets a screen
 * build its columns at runtime from tenant configuration instead of hardcoding cells.
 *
 * Owns the four states every list needs — loading, error, empty, populated — using the shared
 * LoadingState / EmptyState components, so no screen has to reimplement them. Sortable headers
 * carry `aria-sort`; clickable rows are reachable by keyboard and activate on Enter or Space,
 * matching the review queue.
 *
 * Performance note for large result sets: this component is not memoized. A screen that renders a
 * few hundred rows next to a live search box should `useMemo` its `columns` array and its
 * `getRowId` / `getRowLabel` / `isRowClickable` callbacks, or keep the search box's per-keystroke
 * state local and pass only the committed value — otherwise every keystroke re-renders every row.
 *
 * Use this instead of raw `@mui/material/Table`.
 */

import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Tooltip from '@mui/material/Tooltip';
import { SxProps, Theme } from '@mui/material/styles';
import { EmptyState } from './EmptyState';
import { LoadingState } from './LoadingState';
import { focusVisibleStyles } from '../../config/theme';

export type SortDirection = 'asc' | 'desc';

export interface DataTableSort {
  columnId: string;
  direction: SortDirection;
}

export interface DataTableColumn<T> {
  /** Stable identifier — also the value handed to `onSortChange`. */
  id: string;
  /** Column header text. */
  label: string;
  align?: 'left' | 'center' | 'right';
  width?: number | string;
  /** Renders the header as a sort control. Sorting itself stays with the caller. */
  sortable?: boolean;
  /**
   * Cell content for this column. Explicit by design — no implicit key lookup.
   *
   * Optional only when `getCellText` is supplied: a plain-text column needs one accessor, not two
   * that must agree.
   */
  render?: (row: T) => React.ReactNode;
  /**
   * Clips the cell to `maxWidth` with an ellipsis and reveals the whole value in a tooltip,
   * matching the convention already in `ReportsPage`. For columns carrying arbitrary extracted
   * strings — addresses, party names, legal descriptions — that would otherwise set the column
   * width from the longest value in the page.
   *
   * Requires `getCellText` — a tooltip needs a string, and `render` may return an element.
   */
  truncate?: boolean;
  /**
   * Plain-text value of the cell.
   *
   * Supply this ALONE for a text column — it is then both the rendered content and the truncation
   * tooltip, computed once. Supply it ALONGSIDE `render` only when the cell renders an element (a
   * badge, a link) that a tooltip cannot title, since a tooltip needs a string.
   */
  getCellText?: (row: T) => string;
}

const ARIA_SORT: Record<SortDirection, 'ascending' | 'descending'> = {
  asc: 'ascending',
  desc: 'descending',
};

// Hoisted: one style object shared by every clickable row rather than one allocated per row.
const CLICKABLE_ROW_SX = (theme: Theme) => ({
  cursor: 'pointer',
  // A focusable row is not a ButtonBase, so it misses the theme's global focus ring. Inset it so
  // it stays inside the row rather than overlapping its neighbours.
  '&:focus-visible': focusVisibleStyles(theme, '-2px'),
});

/** Rough advance width of one character at the table's body font. Only decides whether a value is
 *  short enough that it cannot possibly be clipped, so an approximation is the right tool — it is
 *  never used to size anything. */
const APPROX_CHAR_WIDTH_PX = 8;

/** Shared empty destructuring source, so a table without `getRowProps` allocates nothing per row. */
const EMPTY_ROW_PROPS: DataTableRowProps = {};

// Clipping needs a block box with a width to clip against; the cell's own `width` is a hint the
// table may exceed, so the constraint sits on the content.
const TRUNCATE_SX = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

/**
 * Clip a cell that asked for it, revealing the full value in a tooltip.
 *
 * No `aria-label` here: `text-overflow: ellipsis` clips only what is PAINTED, so the full string
 * is already in the accessibility tree and a screen reader reads all of it. (An `aria-label` on a
 * `generic` span is prohibited by ARIA in HTML and ignored by assistive tech anyway — it would
 * look like a safeguard while doing nothing.) The tooltip is a sighted-user affordance for text
 * the ellipsis hid, not an accessibility fallback.
 */
function renderCell<T>(column: DataTableColumn<T>, row: T): React.ReactNode {
  // Computed at most once per cell. When only `getCellText` is given it is also the content, so a
  // text column cannot render one string and describe another.
  const text = column.getCellText?.(row);
  const content = column.render ? column.render(row) : text;

  if (!column.truncate || !text) return content;

  // Only reach for a Tooltip when the text plausibly overflows. MUI's Tooltip is not a thin
  // wrapper — useId, four timers, a cloned child with ~8 handlers — and this table is neither
  // memoized nor virtualized over a result set that grows as pages load. Short values cannot be
  // clipped, so wrapping them buys nothing and costs a mount per cell.
  const widthPx = typeof column.width === 'number' ? column.width : undefined;
  const mightOverflow = widthPx === undefined || text.length > widthPx / APPROX_CHAR_WIDTH_PX;

  const clipped = (
    <Box component="span" sx={{ ...TRUNCATE_SX, maxWidth: column.width }}>
      {content}
    </Box>
  );

  return mightOverflow ? <Tooltip title={text}>{clipped}</Tooltip> : clipped;
}

/**
 * Flips the direction when the same column is clicked again, otherwise starts a new column at
 * ascending. Exported because every caller of `onSortChange` needs exactly this.
 */
export function toggleSort(current: DataTableSort | undefined, columnId: string): DataTableSort {
  return current?.columnId === columnId
    ? { columnId, direction: current.direction === 'asc' ? 'desc' : 'asc' }
    : { columnId, direction: 'asc' };
}

/**
 * Row-level props a caller may merge onto a `<tr>` via `getRowProps`.
 *
 * Deliberately NOT `React.HTMLAttributes<HTMLTableRowElement>`: that would let a caller set `id`,
 * `role`, `aria-hidden` or `onFocus` on a row this table is responsible for, and the promise that
 * a caller can only EXTEND a row would be a comment rather than a type. This list is the
 * drag-and-drop surface and nothing else.
 */
export type DataTableRowProps = Pick<
  React.HTMLAttributes<HTMLTableRowElement>,
  'onDragStart' | 'onDragOver' | 'onDragLeave' | 'onDrop' | 'onDragEnd'
> & {
  draggable?: boolean;
  sx?: SxProps<Theme>;
};

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  getRowId: (row: T) => string;
  /** Accessible name for the table. Required — an unnamed table is unusable on a screen reader. */
  ariaLabel: string;
  size?: 'small' | 'medium';

  /**
   * Replaces the rows with the loading state. For the FIRST page only — using it to fetch a
   * further page would wipe the rows already on screen and lose the reader's place. Use
   * `loadingMore` for that.
   */
  loading?: boolean;
  loadingMessage?: string;
  /** Appends progress below the existing rows, e.g. while a continuation token is being followed. */
  loadingMore?: boolean;

  /** Set when the load failed. Takes precedence over the empty state. */
  errorMessage?: string;
  errorDescription?: string;
  /** Overrides the error-tone icon EmptyState supplies for a failure. */
  errorIcon?: React.ReactNode;
  /** Adds a retry action to the error state. */
  onRetry?: () => void;
  retryLabel?: string;

  /** Shown when there are no rows and nothing failed. Required — a blank table is never acceptable. */
  emptyMessage: string;
  emptyDescription?: string;
  emptyIcon?: React.ReactNode;
  emptyAction?: React.ReactNode;

  /** Current sort. Controlled — the caller sorts the rows it passes in, typically via `toggleSort`. */
  sort?: DataTableSort;
  onSortChange?: (columnId: string) => void;

  onRowClick?: (row: T) => void;
  /** Lets a caller keep specific rows non-interactive, e.g. a row locked by another user. */
  isRowClickable?: (row: T) => boolean;
  /**
   * Accessible name for a clickable row, e.g. "Open document 2026-004182". Without it a keyboard
   * user hears the cell contents with no indication that Enter does anything.
   */
  getRowLabel?: (row: T) => string;

  /**
   * Extra DOM props merged onto the row's `<tr>`, for row-level interactions this table does not
   * own — drag-to-reorder being the case it was added for, since a drop target has to be the row
   * itself and no `render` on a column can reach it.
   *
   * Applied BEFORE the table's own row props, so the table's click/keyboard/`aria-label` handling
   * always wins a collision — a caller can extend a row, never quietly disable its accessibility.
   * `sx` is the exception and is MERGED, since clobbering the focus-ring style would be a
   * regression a caller could not see.
   */
  getRowProps?: (row: T, index: number) => DataTableRowProps;

  /** Rendered below the table, outside its horizontal scroll — pagination, totals, "load more". */
  footer?: React.ReactNode;
}

export function DataTable<T>({
  columns,
  rows,
  getRowId,
  ariaLabel,
  size = 'small',
  loading = false,
  loadingMessage,
  loadingMore = false,
  errorMessage,
  errorDescription,
  errorIcon,
  onRetry,
  retryLabel = 'Retry',
  emptyMessage,
  emptyDescription,
  emptyIcon,
  emptyAction,
  sort,
  onSortChange,
  onRowClick,
  isRowClickable,
  getRowLabel,
  getRowProps,
  footer,
}: DataTableProps<T>) {
  const rowClickable = (row: T): boolean =>
    Boolean(onRowClick) && (isRowClickable ? isRowClickable(row) : true);

  const placeholderRow = (content: React.ReactNode, key?: string) => (
    <TableRow key={key}>
      <TableCell colSpan={columns.length} sx={{ border: 0 }}>
        {content}
      </TableCell>
    </TableRow>
  );

  const renderBody = () => {
    if (loading) {
      return placeholderRow(<LoadingState message={loadingMessage} />);
    }

    if (errorMessage) {
      return placeholderRow(
        <EmptyState
          role="alert"
          icon={errorIcon}
          message={errorMessage}
          description={errorDescription}
          action={
            onRetry ? (
              <Button variant="contained" disableElevation onClick={onRetry}>
                {retryLabel}
              </Button>
            ) : undefined
          }
        />,
      );
    }

    if (rows.length === 0) {
      return placeholderRow(
        <EmptyState
          icon={emptyIcon}
          message={emptyMessage}
          description={emptyDescription}
          action={emptyAction}
        />,
      );
    }

    const dataRows = rows.map((row, index) => {
      const clickable = rowClickable(row);
      const extra = getRowProps?.(row, index);
      const { sx: extraSx, ...extraRowProps } = extra ?? EMPTY_ROW_PROPS;
      return (
        <TableRow
          key={getRowId(row)}
          {...extraRowProps}
          // Also the selector the theme keys row hover off, so a non-clickable row gets none.
          hover={clickable}
          tabIndex={clickable ? 0 : undefined}
          aria-label={clickable ? getRowLabel?.(row) : undefined}
          onClick={clickable ? () => onRowClick?.(row) : undefined}
          onKeyDown={
            clickable
              ? (event: React.KeyboardEvent) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onRowClick?.(row);
                  }
                }
              : undefined
          }
          // Array form ONLY when a caller actually supplied `sx` — otherwise keep the hoisted
          // reference (or `undefined`), so a table with no `getRowProps` allocates nothing per row.
          sx={extraSx ? ([clickable && CLICKABLE_ROW_SX, extraSx] as SxProps<Theme>) : (clickable ? CLICKABLE_ROW_SX : undefined)}
        >
          {columns.map((column) => (
            <TableCell key={column.id} align={column.align}>
              {renderCell(column, row)}
            </TableCell>
          ))}
        </TableRow>
      );
    });

    if (loadingMore) {
      dataRows.push(
        placeholderRow(<LoadingState size="small" message={loadingMessage} />, 'loading-more'),
      );
    }

    return dataRows;
  };

  return (
    <Paper variant="outlined">
      {/* The theme gives TableContainer its own border; the Paper above already draws one. */}
      <TableContainer sx={{ border: 0 }}>
        <Table size={size} aria-label={ariaLabel} aria-busy={loading || loadingMore}>
          <TableHead>
            <TableRow>
              {columns.map((column) => {
                const active = sort?.columnId === column.id;
                const sortable = Boolean(column.sortable && onSortChange);
                const direction = active && sort ? sort.direction : 'asc';
                return (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    sx={{ width: column.width }}
                    aria-sort={sortable ? (active ? ARIA_SORT[direction] : 'none') : undefined}
                  >
                    {sortable ? (
                      <TableSortLabel
                        active={active}
                        direction={direction}
                        onClick={() => onSortChange?.(column.id)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>{renderBody()}</TableBody>
        </Table>
      </TableContainer>

      {/* Outside TableContainer so it stays put when a wide table scrolls horizontally. */}
      {footer && <Box sx={{ borderTop: 1, borderColor: 'divider' }}>{footer}</Box>}
    </Paper>
  );
}
